import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { parse, isPast, isToday } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CalendarDays, MapPin, Clock, Loader2, Globe,
  Store, Building2, ExternalLink, Plus, CalendarPlus, Heart, Navigation,
} from "lucide-react";
import { SocialMediaIcons } from "@/components/SocialMediaIcons";
import { EventDescription } from "@/components/EventDescription";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeUrl } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPE_FILTERS = [
  { value: "all", label: "All Events" },
  { value: "business", label: "Small Business Events" },
  { value: "market", label: "Market Events" },
];

const EVENT_TYPE_LABELS = {
  business: "Small Business Event",
  market: "Market Event",
};

const EVENT_TYPE_STYLES = {
  business: "bg-[#42D78A]/10 text-[#1a1a1a] border border-[#42D78A]/30",
  market: "bg-slate-100 text-slate-700 border border-slate-200",
};

const normalizeEventHostType = (type) => {
  const normalized = String(type || "").toLowerCase();
  if (normalized.includes("market")) return "market";
  if (normalized.includes("business")) return "business";
  return "";
};

const buildFallbackHostInfo = (event, hostType) => ({
  type: hostType,
  business_name: event.submitted_by_name || "Business",
  market_name: event.submitted_by_name || "Market",
  business_description: "",
  description: "",
  location: event.location || "",
  address: event.location || "",
  tiktok: "",
});

const getHostDescription = (host) => (
  host?.business_description
  || host?.description
  || host?.about
  || ""
);

const getWebsiteUrl = (website) => normalizeUrl(website);

const HostLinks = ({ host }) => {
  const websiteUrl = getWebsiteUrl(host?.website);

  if (!websiteUrl && !host?.instagram && !host?.facebook && !host?.tiktok) return null;

  return (
    <div className="flex items-center justify-center gap-3 pt-1">
      {websiteUrl && (
        <a
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-9 h-9 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center hover:bg-[#42D78A] hover:text-[#1a1a1a] transition-colors"
          title="Website"
        >
          <Globe className="w-4 h-4" />
        </a>
      )}
      <SocialMediaIcons instagram={host?.instagram} facebook={host?.facebook} tiktok={host?.tiktok} />
    </div>
  );
};

const findMatchingHost = (hosts, event, hostType) => {
  const submittedEmail = event.submitted_by_email?.toLowerCase();
  const submittedName = event.submitted_by_name?.toLowerCase();

  return hosts.find((host) => {
    const hostEmail = (host.business_email || host.email || "").toLowerCase();
    const hostName = (
      hostType === "business" ? host.business_name : host.market_name
    )?.toLowerCase();

    return (submittedEmail && hostEmail === submittedEmail) || (submittedName && hostName === submittedName);
  });
};

const tryParseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Try "MMMM d, yyyy" (from our date picker)
    let d = parse(dateStr, "MMMM d, yyyy", new Date());
    if (!isNaN(d.getTime())) return d;
    // Try native Date parse as fallback
    d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
};

const isUpcoming = (dateStr) => {
  const d = tryParseDate(dateStr);
  if (!d) return true; // No date = show it
  return isToday(d) || !isPast(d);
};

const getTimeMinutes = (timeStr) => {
  const match = String(timeStr || "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return hour * 60 + minute;
};

const getEventTimestamp = (event) => {
  const date = tryParseDate(event.date);
  if (!date) return Number.POSITIVE_INFINITY;
  const sortedDate = new Date(date);
  sortedDate.setHours(0, getTimeMinutes(event.time), 0, 0);
  return sortedDate.getTime();
};

const sortEventsChronologically = (items) => [...items].sort((a, b) => {
  return getEventTimestamp(a) - getEventTimestamp(b);
});

const formatEventTimeRange = (event) => {
  if (event.time && event.end_time) return `${event.time} - ${event.end_time}`;
  return event.time || "";
};

const pad = (value) => String(value).padStart(2, "0");

const parseEventDateTime = (event, value) => {
  const date = tryParseDate(event.date);
  if (!date) return null;

  const minutes = getTimeMinutes(value);
  const dateTime = new Date(date);
  dateTime.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return dateTime;
};

const formatCalendarDate = (date) => {
  if (!date) return "";
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
};

const formatIcsDate = (date) => {
  if (!date) return "";
  return [
    date.getUTCFullYear(),
    pad(date.getUTCMonth() + 1),
    pad(date.getUTCDate()),
    "T",
    pad(date.getUTCHours()),
    pad(date.getUTCMinutes()),
    pad(date.getUTCSeconds()),
    "Z",
  ].join("");
};

const escapeIcsText = (value) => String(value || "")
  .replace(/\\/g, "\\\\")
  .replace(/\n/g, "\\n")
  .replace(/,/g, "\\,")
  .replace(/;/g, "\\;");

const getEventEndDateTime = (event, startDate) => {
  if (!startDate) return null;
  const endDate = event.end_time ? parseEventDateTime(event, event.end_time) : null;
  if (endDate && endDate > startDate) return endDate;
  return new Date(startDate.getTime() + 60 * 60 * 1000);
};

const getCalendarLinks = (event) => {
  const startDate = parseEventDateTime(event, event.time);
  if (!startDate) return null;

  const endDate = getEventEndDateTime(event, startDate);
  const details = [event.description, event.event_link ? `More info: ${normalizeUrl(event.event_link)}` : ""].filter(Boolean).join("\n\n");
  const googleParams = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title || "614 Shop Local Event",
    dates: `${formatCalendarDate(startDate)}/${formatCalendarDate(endDate)}`,
    details,
    location: event.location || "",
  });
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//614 Shop Local//Events//EN",
    "BEGIN:VEVENT",
    `UID:${event.id || Date.now()}@614shoplocal.com`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    `SUMMARY:${escapeIcsText(event.title || "614 Shop Local Event")}`,
    `DESCRIPTION:${escapeIcsText(details)}`,
    `LOCATION:${escapeIcsText(event.location || "")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  return {
    google: `https://calendar.google.com/calendar/render?${googleParams.toString()}`,
    ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`,
  };
};

const getMapUrl = (location) => {
  if (!location) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

const EventsPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hostInfo, setHostInfo] = useState(null);
  const [loadingHost, setLoadingHost] = useState(false);
  const [showHost, setShowHost] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [savedBusinessIds, setSavedBusinessIds] = useState([]);
  const [savedMarketIds, setSavedMarketIds] = useState([]);
  const isAdmin = !!localStorage.getItem("bizkeep_admin_token");
  const isShopper = user?.userType === "shopper";

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Filter to only upcoming events
        const upcoming = sortEventsChronologically(response.data.filter(e => isUpcoming(e.date)));
        setEvents(upcoming);
      } catch (error) {
        console.error("Failed to fetch events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchShopper = async () => {
      if (!isShopper || !user?.token) return;
      try {
        const response = await axios.get(`${API}/shopper`, { headers: { Authorization: `Bearer ${user.token}` } });
        setSavedBusinessIds(response.data.saved_business_ids || []);
        setSavedMarketIds(response.data.saved_market_ids || []);
      } catch {
        setSavedBusinessIds([]);
        setSavedMarketIds([]);
      }
    };
    fetchShopper();
  }, [isShopper, user?.token]);

  const filteredEvents = useMemo(() => {
    if (selectedType === "all") return events;
    return events.filter((event) => normalizeEventHostType(event.submitted_by_type) === selectedType);
  }, [events, selectedType]);

  const handleHostClick = async (event) => {
    const hostType = normalizeEventHostType(event.submitted_by_type);
    if (!hostType) return;
    setLoadingHost(true);
    setShowHost(true);
    const identifiers = Array.from(new Set([
      event.submitted_by_email,
      event.submitted_by_name,
    ].filter(Boolean)));

    try {
      let host = null;

      for (const identifier of identifiers) {
        try {
          const response = await axios.get(`${API}/events/host/${hostType}/${encodeURIComponent(identifier)}`);
          host = response.data;
          break;
        } catch { /* try next identifier */ }
      }

      const directoryEndpoint = hostType === "business" ? "/directory" : "/markets/directory";
      try {
        const directoryResponse = await axios.get(`${API}${directoryEndpoint}`);
        const directoryHost = findMatchingHost(directoryResponse.data, event, hostType);
        if (directoryHost) host = { ...host, ...directoryHost };
      } catch { /* directory enrichment is optional */ }

      setHostInfo(host ? { ...host, type: hostType } : buildFallbackHostInfo(event, hostType));
    } catch {
      setHostInfo(buildFallbackHostInfo(event, hostType));
    } finally {
      setLoadingHost(false);
    }
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith("http")) return logo;
    return `${BACKEND_URL}${logo}`;
  };

  const handleFollowBusiness = async (businessId) => {
    if (!isAuthenticated) {
      window.location.href = "/shopper-signup";
      return;
    }
    if (!isShopper) {
      toast.error("Use a shopper profile to save businesses.");
      return;
    }

    try {
      const response = await axios.post(`${API}/shopper/follow/business/${businessId}`, {}, { headers: { Authorization: `Bearer ${user.token}` } });
      setSavedBusinessIds(response.data.saved_business_ids || []);
      toast.success(response.data.following ? "Business saved." : "Business removed.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update saved business");
    }
  };

  const handleFollowMarket = async (marketId) => {
    if (!isAuthenticated) {
      window.location.href = "/shopper-signup";
      return;
    }
    if (!isShopper) {
      toast.error("Use a shopper profile to follow markets.");
      return;
    }

    try {
      const response = await axios.post(`${API}/shopper/follow/market/${marketId}`, {}, { headers: { Authorization: `Bearer ${user.token}` } });
      setSavedMarketIds(response.data.saved_market_ids || []);
      toast.success(response.data.following ? "Market followed." : "Market removed.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update followed market");
    }
  };

  const selectedCalendarLinks = selectedEvent ? getCalendarLinks(selectedEvent) : null;

  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Events</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Upcoming Events</h1>
          <p className="text-gray-600 max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Discover upcoming local events from businesses and markets in Columbus.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/directory">
              <Button
                className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="events-business-directory-link"
              >
                View Business Directory
              </Button>
            </Link>
            <Link to="/markets">
              <Button
                variant="outline"
                className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="events-market-directory-link"
              >
                View Market Directory
              </Button>
            </Link>
            {isAdmin && (
              <Link to="/admin/dashboard?section=events">
                <Button
                  variant="outline"
                  className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="events-admin-add-event-link"
                >
                  <Plus className="w-4 h-4" /> Add Event
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {!loading && (
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {EVENT_TYPE_FILTERS.map((filter) => (
                <Button
                  key={filter.value}
                  variant="outline"
                  onClick={() => setSelectedType(filter.value)}
                  className={`rounded-full border px-5 py-2 text-sm font-bold ${
                    selectedType === filter.value
                      ? "bg-[#1a1a1a] text-white border-[#1a1a1a] hover:bg-black hover:text-white"
                      : "bg-white text-[#1a1a1a] border-gray-200 hover:border-[#42D78A] hover:bg-[#42D78A]/10"
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid={`event-filter-${filter.value}`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>No upcoming events</h3>
              <p className="text-gray-500 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>Check back soon for new events!</p>
            </div>
          ) : (
            <div className="space-y-6 stagger-in">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className="bg-white border border-gray-100 rounded-2xl p-8 hover:shadow-lg transition-all cursor-pointer hover:border-[#42D78A]/30"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-[#42D78A]/10 flex items-center justify-center">
                      <CalendarDays className="w-7 h-7 text-[#42D78A]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{event.title}</h3>
                        {EVENT_TYPE_LABELS[normalizeEventHostType(event.submitted_by_type)] && (
                          <Badge className={`rounded-full px-3 py-1 text-xs font-bold ${EVENT_TYPE_STYLES[normalizeEventHostType(event.submitted_by_type)]}`}>
                            {EVENT_TYPE_LABELS[normalizeEventHostType(event.submitted_by_type)]}
                          </Badge>
                        )}
                      </div>
                      <EventDescription
                        text={event.description}
                        preview
                        className="text-gray-600 text-sm mb-4"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      />
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {event.date && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {event.date}</span>}
                        {formatEventTimeRange(event) && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {formatEventTimeRange(event)}</span>}
                        {event.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>}
                      </div>
                      {event.submitted_by_name && (
                        <p className="mt-3 text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                          Hosted by <span className="font-medium text-[#42D78A]">{event.submitted_by_name}</span>
                        </p>
                      )}
                      {event.event_link && (
                        <a
                          href={normalizeUrl(event.event_link)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(clickEvent) => clickEvent.stopPropagation()}
                          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#42D78A] px-5 py-2 text-sm font-bold text-[#1a1a1a] transition-all hover:-translate-y-0.5 hover:bg-[#3bc27c]"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                          data-testid={`event-learn-more-${event.id}`}
                        >
                          Learn more <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-16 py-12 bg-gray-50 rounded-2xl">
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Have an upcoming event?</h3>
            <p className="text-gray-500 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Registered businesses and markets can submit events from their dashboard.
            </p>
            <Link to="/login">
              <Button className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-10 py-5 text-sm font-bold transition-all hover:-translate-y-0.5" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="events-login-btn">
                Login to Submit an Event
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-5 mt-2">
              {EVENT_TYPE_LABELS[normalizeEventHostType(selectedEvent.submitted_by_type)] && (
                <Badge className={`rounded-full px-3 py-1 text-xs font-bold ${EVENT_TYPE_STYLES[normalizeEventHostType(selectedEvent.submitted_by_type)]}`}>
                  {EVENT_TYPE_LABELS[normalizeEventHostType(selectedEvent.submitted_by_type)]}
                </Badge>
              )}
              <EventDescription
                text={selectedEvent.description}
                className="text-gray-600 text-sm leading-relaxed"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              />
              <div className="grid grid-cols-1 gap-3">
                {selectedEvent.date && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <CalendarDays className="w-5 h-5 text-[#42D78A]" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>Date</p>
                      <p className="text-sm font-medium text-[#1a1a1a]">{selectedEvent.date}</p>
                    </div>
                  </div>
                )}
                {formatEventTimeRange(selectedEvent) && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Clock className="w-5 h-5 text-[#42D78A]" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>Time</p>
                      <p className="text-sm font-medium text-[#1a1a1a]">{formatEventTimeRange(selectedEvent)}</p>
                    </div>
                  </div>
                )}
                {selectedEvent.location && (
                  <a
                    href={getMapUrl(selectedEvent.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl transition-colors hover:bg-[#42D78A]/10"
                    data-testid="event-location-map-link"
                  >
                    <MapPin className="w-5 h-5 text-[#42D78A]" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>Location</p>
                      <p className="text-sm font-medium text-[#1a1a1a]">{selectedEvent.location}</p>
                    </div>
                    <span className="hidden items-center gap-1 text-xs font-bold text-gray-400 sm:inline-flex" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Get directions <Navigation className="w-4 h-4" />
                    </span>
                    <Navigation className="w-4 h-4 text-gray-400 sm:hidden" />
                  </a>
                )}
              </div>

              {selectedCalendarLinks && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <a
                    href={selectedCalendarLinks.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-[#1a1a1a] transition-colors hover:border-[#42D78A] hover:bg-[#42D78A]/10"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="event-add-google-calendar"
                  >
                    <CalendarPlus className="w-4 h-4" /> Google Calendar
                  </a>
                  <a
                    href={selectedCalendarLinks.ics}
                    download={`${(selectedEvent.title || "614-shop-local-event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-[#1a1a1a] transition-colors hover:border-[#42D78A] hover:bg-[#42D78A]/10"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="event-add-ics-calendar"
                  >
                    <CalendarDays className="w-4 h-4" /> Apple / iCloud / Outlook
                  </a>
                </div>
              )}

              {selectedEvent.event_link && (
                <a
                  href={normalizeUrl(selectedEvent.event_link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#42D78A] px-6 py-3 text-sm font-bold text-[#1a1a1a] transition-all hover:-translate-y-0.5 hover:bg-[#3bc27c]"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="event-detail-learn-more"
                >
                  Learn more <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {/* Host Info */}
              {selectedEvent.submitted_by_name && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Hosted by</p>
                  <button
                    onClick={() => handleHostClick(selectedEvent)}
                    className="flex items-center gap-3 w-full p-3 bg-gray-50 rounded-xl hover:bg-[#42D78A]/10 transition-colors group text-left"
                    data-testid="event-host-link"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                      {normalizeEventHostType(selectedEvent.submitted_by_type) === "market" ? (
                        <Store className="w-5 h-5 text-white" />
                      ) : (
                        <Building2 className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#1a1a1a] group-hover:text-[#42D78A] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {selectedEvent.submitted_by_name}
                      </p>
                      <p className="text-xs text-gray-400 capitalize">{normalizeEventHostType(selectedEvent.submitted_by_type)}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#42D78A] transition-colors" />
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Host Detail Modal */}
      <Dialog open={showHost} onOpenChange={(open) => { if (!open) { setShowHost(false); setHostInfo(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-center text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {loadingHost
                ? "Loading..."
                : (hostInfo?.type || normalizeEventHostType(selectedEvent?.submitted_by_type)) === "business"
                  ? "Business Profile"
                  : "Market Profile"}
            </DialogTitle>
          </DialogHeader>
          {loadingHost ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : !hostInfo ? (
            <div className="text-center py-8 text-gray-500">Host information not available.</div>
          ) : hostInfo.type === "business" ? (
            <div className="space-y-4 mt-2">
              {hostInfo.logo && (
                <div className="flex justify-center">
                  <img src={getLogoUrl(hostInfo.logo)} alt="Logo" className="max-h-24 max-w-full object-contain rounded-lg" />
                </div>
              )}
              <h3 className="text-xl font-bold text-center text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{hostInfo.business_name}</h3>
              {getHostDescription(hostInfo) && <p className="text-gray-600 text-sm text-center">{getHostDescription(hostInfo)}</p>}
              <HostLinks host={hostInfo} />
              {hostInfo.id && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleFollowBusiness(hostInfo.id)}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${
                      savedBusinessIds.includes(hostInfo.id)
                        ? "bg-[#42D78A] text-[#1a1a1a]"
                        : "border border-gray-200 bg-white text-gray-600 hover:border-[#42D78A] hover:text-[#1a1a1a]"
                    }`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid={`event-save-business-${hostInfo.id}`}
                  >
                    <Heart className={`w-4 h-4 ${savedBusinessIds.includes(hostInfo.id) ? "fill-current" : ""}`} />
                    {savedBusinessIds.includes(hostInfo.id) ? "Saved" : "Save Business"}
                  </button>
                  <Link
                    to={`/directory?business=${encodeURIComponent(hostInfo.id)}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1a1a1a] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-black"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid={`event-view-business-profile-${hostInfo.id}`}
                  >
                    View Full Profile <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {hostInfo.logo && (
                <div className="flex justify-center">
                  <img src={getLogoUrl(hostInfo.logo)} alt="Logo" className="max-h-24 max-w-full object-contain rounded-lg" />
                </div>
              )}
              <h3 className="text-xl font-bold text-center text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{hostInfo.market_name}</h3>
              {getHostDescription(hostInfo) && <p className="text-gray-600 text-sm text-center">{getHostDescription(hostInfo)}</p>}
              <HostLinks host={hostInfo} />
              {hostInfo.id && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleFollowMarket(hostInfo.id)}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${
                      savedMarketIds.includes(hostInfo.id)
                        ? "bg-[#42D78A] text-[#1a1a1a]"
                        : "border border-gray-200 bg-white text-gray-600 hover:border-[#42D78A] hover:text-[#1a1a1a]"
                    }`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid={`event-save-market-${hostInfo.id}`}
                  >
                    <Heart className={`w-4 h-4 ${savedMarketIds.includes(hostInfo.id) ? "fill-current" : ""}`} />
                    {savedMarketIds.includes(hostInfo.id) ? "Following" : "Follow Market"}
                  </button>
                  <Link
                    to={`/markets?market=${encodeURIComponent(hostInfo.id)}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1a1a1a] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-black"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid={`event-view-market-profile-${hostInfo.id}`}
                  >
                    View Full Profile <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsPage;
